(ns hai.journal2.integration-registry
  "Central registry of timeline integration types and their display properties.

  All type→value lookups (labels, icons, colors, OAuth URLs, backfill config)
  live here so adding a new integration is a single-map-entry change."
  (:require
   [hai.library.connections.create :as create]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]))

;; ---------------------------------------------------------------------------
;; MUI icon defs (shared with timeline.cljs & collection.cljs)
;; ---------------------------------------------------------------------------

(def event-icon (icons/mui-icon :outlined :event))
(def task-icon (icons/mui-icon :outlined :task_alt))

;; ---------------------------------------------------------------------------
;; Registry
;; ---------------------------------------------------------------------------

(def registry
  "Map from timeline-integration type ident → display & behaviour properties."
  {:timeline-integration.type/google-calendar
   {:label              "Google Calendar"
    :entry-type         :journal.entry.type/google-calendar
    :icon-fn            (fn [] [icons/google-calendar])
    :icon-color         "var(--blue-9)"
    :source-color       t/--property-38
    :action-glyph       "+"
    :oauth-url-fn       create/google-calendar-oauth-url
    :default-backfill-days 1095
    :connectable?       true
    :removable?         true
    :source-link-attr   :journal.entry.google-calendar/link
    :show-duration?     true
    :people-source      :gcal}

   :timeline-integration.type/outlook-calendar
   {:label              "Outlook Calendar"
    :entry-type         :journal.entry.type/outlook-calendar
    :icon-fn            (fn [] [icons/outlook-calendar])
    :icon-color         "var(--blue-9)"
    :source-color       t/--property-38
    :action-glyph       "+"
    :oauth-url-fn       create/outlook-calendar-oauth-url
    :default-backfill-days 1095
    :connectable?       true
    :removable?         true
    :source-link-attr   :journal.entry.outlook-calendar/link
    :show-duration?     true
    :people-source      :outlook}

   :timeline-integration.type/linear
   {:label              "Linear"
    :entry-type         :journal.entry.type/linear
    :icon-fn            (fn [] [icons/linear])
    :icon-color         "var(--iris-9)"
    :source-color       t/--property-35
    :action-glyph       "+"
    :oauth-url-fn       create/linear-oauth-url
    :default-backfill-days 1095
    :connectable?       true
    :removable?         true
    :source-link-attr   :journal.entry.linear/link
    :show-duration?     false
    :people-source      :linear}

   :timeline-integration.type/github
   {:label              "GitHub"
    :entry-type         :journal.entry.type/github
    :icon-fn            (fn [] [icons/github])
    :icon-color         "var(--gray-12)"
    :source-color       t/--property-39
    :action-glyph       "+"
    :oauth-url-fn       create/github-oauth-url
    :default-backfill-days 30
    :connectable?       true
    :removable?         true
    :source-link-attr   :journal.entry.github/link
    :show-duration?     false
    :people-source      :github}

   :timeline-integration.type/slack-dm
   {:label              "Slack DMs with @Humble"
    :entry-type         :journal.entry.type/slack-dm
    :icon-fn            (fn [] [icons/slack])
    :icon-color         nil
    :source-color       t/--property-40
    :action-glyph       "✏"
    :connectable?       false
    :removable?         false
    :show-duration?     false
    :people-source      :other}

   :timeline-integration.type/notes
   {:label              "Notes"
    :entry-type         :journal.entry.type/notes
    :icon-fn            (fn [] [icons/sticky-note])
    :icon-color         "var(--green-9)"
    :source-color       t/--property-40
    :action-glyph       "✏"
    :connectable?       false
    :removable?         false
    :show-duration?     false
    :people-source      :other}

   :timeline-integration.type/tasks
   {:label              "Tasks"
    :entry-type         :journal.entry.type/tasks
    :icon-fn            (fn [] [task-icon])
    :icon-color         "var(--green-9)"
    :source-color       t/--property-41
    :connectable?       false
    :removable?         false
    :show-duration?     false
    :people-source      :other}})

;; ---------------------------------------------------------------------------
;; Derived collections
;; ---------------------------------------------------------------------------

(def all-types
  "Set of all timeline-integration type idents."
  (set (keys registry)))

(def all-type-labels
  "Vector of [type-ident label] pairs for filter checkboxes."
  (mapv (fn [[k v]] [k (:label v)]) registry))

(def connectable-types
  "Vector of integration types that have an OAuth connection flow."
  (into [] (comp (filter (comp :connectable? val)) (map key)) registry))

(def non-removable-types
  "Set of integration types that cannot be removed by the user."
  (into #{} (comp (remove (comp :removable? val)) (map key)) registry))

;; ---------------------------------------------------------------------------
;; Reverse lookups
;; ---------------------------------------------------------------------------

(def entry-type->integration-type
  "Map from journal entry type → timeline-integration type."
  (into {} (map (fn [[k v]] [(:entry-type v) k])) registry))

;; ---------------------------------------------------------------------------
;; Helpers
;; ---------------------------------------------------------------------------

(defn integration-type-label
  "Human-readable display name for a timeline-integration type ident."
  [type-ident]
  (get-in registry [type-ident :label] (name type-ident)))

(defn source-color
  "Background color for the spine icon of a given entry type."
  [entry-type]
  (get-in registry [(entry-type->integration-type entry-type) :source-color] t/--property-39))

(defn entry-icon+color
  "Returns [icon-hiccup color-string] for a given entry type."
  [entry-type]
  (let [it   (entry-type->integration-type entry-type)
        spec (get registry it)]
    (if spec
      [((:icon-fn spec)) (or (:icon-color spec) "var(--gray-9)")]
      [[:span] "var(--gray-9)"])))

(defn table-icon
  "Returns the icon hiccup for the collection/integrations table."
  [type-ident]
  (if-let [spec (get registry type-ident)]
    ((or (:table-icon-fn spec) (:icon-fn spec)))
    [:span]))

(defn source-link
  "Returns the navigable source URL for an entry, or nil."
  [entry]
  (let [entry-type (get-in entry [:journal.entry/type :db/ident])
        it         (entry-type->integration-type entry-type)]
    (when-let [attr (get-in registry [it :source-link-attr])]
      (get entry attr))))

(defn oauth-url
  "Generates the OAuth URL for a connectable integration type, or nil."
  [integration-type metadata]
  (when-let [f (get-in registry [integration-type :oauth-url-fn])]
    (f metadata)))

(defn default-backfill-days
  "Default number of days to backfill for a given integration type."
  [integration-type]
  (get-in registry [integration-type :default-backfill-days] 1095))

(defn show-duration?
  "Whether to show duration on entry cards for a given entry type."
  [entry-type]
  (get-in registry [(entry-type->integration-type entry-type) :show-duration?] false))

(defn people-source-icon
  "Renders a small service icon for a person in the filter list.
  Priority: Humble > Linear > GCal > GitHub."
  [{:keys [sources directory?]}]
  (let [wrap (fn [child]
               [:span {:style {:width "14px" :height "14px" :flex "0 0 14px"
                               :display "inline-flex" :align-items "center" :justify-content "center"
                               :overflow "hidden"}}
                child])]
    (cond
      directory? (wrap [icons/humble {:style {:width "14px" :height "14px"}}])
      :else
      (some (fn [[source icon-hiccup]]
              (when (contains? sources source) (wrap icon-hiccup)))
            [[:humble  [icons/humble {:style {:width "14px" :height "14px"}}]]
             [:linear  [icons/linear {:size 14}]]
             [:gcal    [icons/google-calendar {:size 14}]]
             [:outlook [icons/outlook-calendar {:size 14}]]
             [:github  [icons/github {:size 14}]]]))))
